import { Menu, type MenuItem } from "../../components/ui/Menu";
import { Status } from "../../components/ui/Status";
import { useI18n } from "../../i18n/I18nProvider";
import type { ApiKey } from "../../lib/api-types";
import { formatDate } from "./format-date";

export interface ApiKeyRowProps {
  apiKey: ApiKey;
  busy: boolean;
  onRevoke: () => void;
}

/** One key as a label-grid row: label, created and last used, status. Active keys revoke from a menu. */
export function ApiKeyRow({ apiKey, busy, onRevoke }: ApiKeyRowProps) {
  const { t, lang } = useI18n();
  const revoked = apiKey.revoked_at !== null;
  const items: MenuItem[] = [
    { label: t("keys.revoke"), onSelect: onRevoke, tone: "danger", disabled: busy },
  ];
  const lastUsed = apiKey.last_used_at
    ? t("keys.lastUsed", { date: formatDate(apiKey.last_used_at, lang) })
    : t("keys.never");
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1 space-y-1 sm:grid sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] sm:items-center sm:gap-4 sm:space-y-0">
        <p className={`truncate text-operate font-semibold ${revoked ? "text-ink-3" : "text-ink"}`}>
          {apiKey.label}
        </p>
        <p className="truncate text-note text-ink-2">
          {t("keys.created", { date: formatDate(apiKey.created_at, lang) })} · {lastUsed}
        </p>
      </div>
      {revoked ? (
        <Status tone="idle">{t("keys.revoked")}</Status>
      ) : (
        <Status tone="linked">{t("keys.active")}</Status>
      )}
      {revoked ? (
        <span aria-hidden="true" className="size-11 shrink-0" />
      ) : (
        <Menu label={t("keys.moreActions", { name: apiKey.label })} items={items} />
      )}
    </li>
  );
}
